import React, { useState, useEffect, useMemo } from "react";
import { rtdb } from "../firebase";
import { ref, onValue, push, set, remove } from "firebase/database";
import "./AdminStaff.css";

export default function AdminStaff({ onBack }) {
  const [staffList, setStaffList] = useState([]);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [editId, setEditId] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  
  const [sortDirection, setSortDirection] = useState(null); 

  useEffect(() => {
    // Declaring the reference inside the effect guarantees a stable reference boundary
    const staffRef = ref(rtdb, "Staff");

    const unsubscribe = onValue(staffRef, (snapshot) => {
      const data = snapshot.val();
      const list = data
        ? Object.keys(data).map((key) => ({ id: key, ...data[key] }))
        : [];
      setStaffList(list);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleSubmit = () => {
    if (!name.trim() || !phone.trim()) {
      alert("Please fill in all fields");
      return;
    }
    const data = { name: name.trim(), phone: phone.trim() };
    const staffRef = ref(rtdb, "Staff");
    const action = editId 
      ? set(ref(rtdb, `Staff/${editId}`), data) 
      : push(staffRef, data);

    action.then(() => {
        clearForm(); 
      })
      .catch(() => alert("Error saving data"));
  };

  const handleEdit = (staff) => {
    setEditId(staff.id);
    setName(staff.name);
    setPhone(staff.phone);
    setIsFormOpen(true); 
  };

  const handleDelete = (staff) => {
    if (!window.confirm(`Delete ${staff.name}?`)) return;
    
    remove(ref(rtdb, `Staff/${staff.id}`))
      .then(() => {
        if (editId === staff.id) {
          clearForm();
        }
      })
      .catch(() => alert("Failed to delete staff member"));
  };

  const clearForm = () => {
    setEditId("");
    setName("");
    setPhone("");
    setIsFormOpen(false); 
  };

  const processedStaffList = useMemo(() => {
    const query = search.trim().toLowerCase();
    
    let result = staffList.filter((s) =>
      (s.name || "").toLowerCase().includes(query)
    );

    if (sortDirection === "az") {
      result.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    } else if (sortDirection === "za") {
      result.sort((a, b) => (b.name || "").localeCompare(a.name || ""));
    }

    return result;
  }, [staffList, search, sortDirection]);

  return (
    <div className="admin-staff-page">
      {/* 🔹 Glass Header */}
      <div className="glass-header-staff">
        <h2 className="page-title-staff">Staff Directory</h2>
      </div>

      {/* 🔹 Controls */}
      <div className="top-controls-glass">
        <div className="search-wrapper">
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="sort-buttons-modern">
          <button className={sortDirection === "az" ? "active" : ""} onClick={() => setSortDirection("az")}>A-Z</button>
          <button className={sortDirection === "za" ? "active" : ""} onClick={() => setSortDirection("za")}>Z-A</button>
          {sortDirection && <button className="clear-sort" onClick={() => setSortDirection(null)}>Reset</button>}
        </div>
      </div>

      <div className="stats-container">
        <div className="stat-box">
          <span className="stat-count">{processedStaffList.length}</span>
          <span className="stat-label">Staff</span>
        </div>
      </div>
      
      {/* 🔹 Staff Grid / Loading Shimmer */}
      {loading ? (
        <div className="loading-shimmer">
          <p>Syncing Database...</p>
        </div>
      ) : (
        <div className="staff-grid">
          {processedStaffList.length === 0 ? (
            <div className="empty-state">No team members match your criteria.</div>
          ) : (
            processedStaffList.map((staff) => (
              <div key={staff.id} className="modern-staff-card">
                <div className="card-accent"></div>
                <div className="card-content">
                  <div className="staff-avatar-circle">
                    {(staff.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="staff-details">
                    <span className="staff-name">{staff.name}</span>
                    <span className="staff-phone">{staff.phone}</span>
                  </div>
                </div>
                <div className="card-actions-overlay">
                  <button className="btn-icon-edit" onClick={() => handleEdit(staff)}>✎</button>
                  <button className="btn-icon-delete" onClick={() => handleDelete(staff)}>✕</button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* 🔹 Floating Action Button (FAB) */}
      {!isFormOpen && (
        <button className="fab-add" onClick={() => setIsFormOpen(true)}>
          +
        </button>
      )}

      {/* 🔹 Slide-up Drawer Form */}
      <div className={`form-overlay ${isFormOpen ? "active" : ""}`} onClick={clearForm}>
        <div className="staff-form-drawer" onClick={(e) => e.stopPropagation()}>
          <div className="drawer-handle"></div>
          <h3>{editId ? "Update Member" : "Add New Member"}</h3>
          <div className="input-group">
            <input
              type="text"
              placeholder="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <input
              type="text"
              placeholder="Phone Number"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          <div className="form-buttons">
            <button className="btn-primary" onClick={handleSubmit}>
              {editId ? "Save Changes" : "Register Staff"}
            </button>
            <button className="btn-ghost" onClick={clearForm}>Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}